import React, { useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';

const ContractDescriptionForm = ({ onSubmit, onBack, loading }) => {
  const [step, setStep] = useState(1);
  const [description, setDescription] = useState('');
  const [contractType, setContractType] = useState('');
  const [parties, setParties] = useState({
    party1Name: '',
    party1Address: '',
    party2Name: '',
    party2Address: ''
  });
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [error, setError] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (step === 1) {
      if (!description.trim()) {
        setError('Please provide a description of the contract you need');
        return;
      }
      
      // Extract contract type from description if not explicitly provided
      if (!contractType) {
        const lowerDesc = description.toLowerCase();
        if (lowerDesc.includes('employment') || lowerDesc.includes('job') || lowerDesc.includes('hire')) {
          setContractType('Employment Contract');
        } else if (lowerDesc.includes('nda') || lowerDesc.includes('non-disclosure') || lowerDesc.includes('confidentiality')) {
          setContractType('Non-Disclosure Agreement');
        } else if (lowerDesc.includes('service') || lowerDesc.includes('consulting')) {
          setContractType('Service Agreement');
        } else if (lowerDesc.includes('rent') || lowerDesc.includes('lease') || lowerDesc.includes('property')) {
          setContractType('Rental Agreement');
        } else if (lowerDesc.includes('sale') || lowerDesc.includes('purchase') || lowerDesc.includes('buy')) {
          setContractType('Sales Contract');
        }
      }
      
      setStep(2);
      return;
    }
    
    if (step === 2) {
      if (!contractType.trim()) {
        setError('Please specify the type of contract');
        return;
      }
      setStep(3);
      return;
    }
    
    if (step === 3) {
      if (!parties.party1Name.trim() || !parties.party2Name.trim()) {
        setError('Please provide names for both parties');
        return;
      }
      
      // Combine all information into a structured description
      const fullDescription = `
Contract Type: ${contractType}

Party 1: ${parties.party1Name}${parties.party1Address ? `\nAddress: ${parties.party1Address}` : ''}
Party 2: ${parties.party2Name}${parties.party2Address ? `\nAddress: ${parties.party2Address}` : ''}

Original Description: ${description}

Additional Details: ${additionalDetails}
      `.trim();
      
      onSubmit(fullDescription);
    }
  };
  
  // Example prompts to help users
  const examplePrompts = [
    "I need an employment contract for a new software developer starting next month",
    "Create a non-disclosure agreement between my company and a contractor",
    "I need a service agreement for my consulting business",
    "Create a rental agreement for my property in New York",
    "I need a sales contract for selling my handmade products online"
  ];
  
  const handleExampleClick = (example) => {
    setDescription(example);
    setError('');
  };
  
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      onBack();
    }
  };
  
  return (
    <div className="contract-description-form">
      <h2>Describe Your Contract</h2>
      <p>
        Tell us what kind of contract you need, and we'll guide you through the process.
      </p>
      
      <div className="step-indicator">
        <div className={`step ${step >= 1 ? 'active' : ''}`}>1. Description</div>
        <div className={`step ${step >= 2 ? 'active' : ''}`}>2. Contract Type</div>
        <div className={`step ${step >= 3 ? 'active' : ''}`}>3. Parties</div>
      </div>
      
      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <>
            <div className="form-group">
              <label htmlFor="contract-description">Contract Description</label>
              <textarea
                id="contract-description"
                className={`form-control ${error ? 'is-invalid' : ''}`}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setError('');
                }}
                placeholder="Describe the contract you need..."
                rows={6}
                disabled={loading}
              />
              {error && <div className="invalid-feedback">{error}</div>}
            </div>
            
            <div className="example-prompts">
              <h3>Examples</h3>
              <p>Click on any example to use it as a starting point:</p>
              <div className="example-buttons">
                {examplePrompts.map((example, index) => (
                  <button
                    key={index}
                    type="button"
                    className="example-button"
                    onClick={() => handleExampleClick(example)}
                    disabled={loading}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        
        {step === 2 && (
          <div className="form-group">
            <label htmlFor="contract-type">Contract Type</label>
            <input
              id="contract-type"
              className={`form-control ${error ? 'is-invalid' : ''}`}
              value={contractType}
              onChange={(e) => {
                setContractType(e.target.value);
                setError('');
              }}
              placeholder="e.g., Employment Contract, NDA, Service Agreement, etc."
              disabled={loading}
            />
            {error && <div className="invalid-feedback">{error}</div>}
            
            <div className="contract-type-suggestions">
              <p>Common contract types:</p>
              <div className="suggestion-buttons">
                {['Employment Contract', 'Non-Disclosure Agreement', 'Service Agreement', 
                  'Rental Agreement', 'Sales Contract'].map((type, index) => (
                  <button
                    key={index}
                    type="button"
                    className="suggestion-button"
                    onClick={() => setContractType(type)}
                    disabled={loading}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {step === 3 && (
          <>
            <div className="parties-section">
              <h3>Party 1</h3>
              <div className="form-group">
                <label htmlFor="party1-name">Name</label>
                <input
                  id="party1-name"
                  className={`form-control ${error && !parties.party1Name ? 'is-invalid' : ''}`}
                  value={parties.party1Name}
                  onChange={(e) => {
                    setParties({...parties, party1Name: e.target.value});
                    setError('');
                  }}
                  placeholder="Full name or company name"
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="party1-address">Address (Optional)</label>
                <input
                  id="party1-address"
                  className="form-control"
                  value={parties.party1Address}
                  onChange={(e) => setParties({...parties, party1Address: e.target.value})}
                  placeholder="Full address"
                  disabled={loading}
                />
              </div>
              
              <h3>Party 2</h3>
              <div className="form-group">
                <label htmlFor="party2-name">Name</label>
                <input
                  id="party2-name"
                  className={`form-control ${error && !parties.party2Name ? 'is-invalid' : ''}`}
                  value={parties.party2Name}
                  onChange={(e) => {
                    setParties({...parties, party2Name: e.target.value});
                    setError('');
                  }}
                  placeholder="Full name or company name"
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="party2-address">Address (Optional)</label>
                <input
                  id="party2-address"
                  className="form-control"
                  value={parties.party2Address}
                  onChange={(e) => setParties({...parties, party2Address: e.target.value})}
                  placeholder="Full address"
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="additional-details">Additional Details (Optional)</label>
              <textarea
                id="additional-details"
                className="form-control"
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                placeholder="Any other details you'd like to include in the contract..."
                rows={4}
                disabled={loading}
              />
            </div>
            
            {error && <div className="alert alert-danger">{error}</div>}
          </>
        )}
        
        <div className="form-actions">
          <button 
            type="button"
            className="btn btn-secondary"
            onClick={handleBack}
            disabled={loading}
          >
            {step > 1 ? 'Previous' : 'Back'}
          </button>
          <button 
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || (step === 1 && !description.trim()) || 
                     (step === 2 && !contractType.trim()) ||
                     (step === 3 && (!parties.party1Name.trim() || !parties.party2Name.trim()))}
          >
            {loading ? 'Loading...' : (step < 3 ? 'Next' : 'Generate Contract')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContractDescriptionForm;
